/**
 * Count-up support for the Stats Strip. A stat is free text ("1,000+", "50K+",
 * "99.9%", "4.8/5"); we animate only its numeric part and keep everything
 * else — prefix, suffix, decimals, thousands grouping — exactly as written.
 * Pure, so the shape rules are unit-tested and the component only animates.
 */
export interface ParsedStat {
  prefix: string;
  value: number;
  decimals: number;
  grouped: boolean;
  suffix: string;
}

// Numeric part = en-US grouped integer (1,000,000) OR plain digits, optional
// .decimals. Anything else after it — including a stray comma such as the
// European "1,5" — is suffix, so the authored text always round-trips.
// `[\s\S]` rather than `.` with the `s` flag: tsconfig targets ES2017 (TS1501).
// (?!\d): a grouped integer may not run straight into more digits — "1,2345" is not "1,234"+"5".
const STAT_RE = /^([^\d]*?)(\d{1,3}(?:,\d{3})+(?!\d)|\d+)(\.\d+)?([\s\S]*)$/;

/** null when the text has no number (rendered as-is, no animation). */
export function parseStat(text: string): ParsedStat | null {
  const m = STAT_RE.exec(text);
  if (!m) return null;
  const [, prefix, int, frac = '', suffix] = m;
  return {
    prefix,
    value: Number(int.replace(/,/g, '') + frac),
    decimals: frac ? frac.length - 1 : 0,
    grouped: int.includes(','),
    suffix,
  };
}

/** Format `n` in the shape of `parsed` — same decimals, grouping and affixes. */
export function formatStat(parsed: ParsedStat, n: number): string {
  const body = n.toLocaleString('en-US', {
    minimumFractionDigits: parsed.decimals,
    maximumFractionDigits: parsed.decimals,
    useGrouping: parsed.grouped,
  });
  return `${parsed.prefix}${body}${parsed.suffix}`;
}

/** Fast start, gentle landing. t ∈ [0, 1]. */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
